import React, { useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import SupportButton from '../components/SupportButton';
import EmailCaptureModal from '../components/EmailCaptureModal';
import Header from '../components/Header';
import { useAuth } from '../contexts/AuthContext';
import { 
  Upload,
  Loader2,
  Phone,
  CheckCircle,
  Download,
  ArrowRight,
  Shield,
  Zap,
  AlertCircle,
  Sparkles,
  Globe,
  FileSpreadsheet
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PhoneFormatterPage = () => {
  const [file, setFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [columns, setColumns] = useState([]);
  const [selectedColumn, setSelectedColumn] = useState('');
  const [loadingColumns, setLoadingColumns] = useState(false);
  const fileInputRef = useRef(null);
  const { user } = useAuth();

  const handleFileSelect = useCallback(async (selectedFile) => {
    if (!selectedFile) return;
    
    const validTypes = ['.csv', '.xlsx', '.xls'];
    const fileExt = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf('.'));
    
    if (!validTypes.includes(fileExt)) {
      toast.error('Please upload a CSV or Excel file');
      return;
    }
    
    if (selectedFile.size > 50 * 1024 * 1024) {
      toast.error('File size must be under 50MB');
      return;
    }
    
    setFile(selectedFile);
    setResult(null);
    setColumns([]);
    setSelectedColumn('');
    
    // Get columns from file
    setLoadingColumns(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await axios.post(
        `${API_URL}/api/tools/phone-formatter/columns`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 30000 }
      );
      
      setColumns(response.data.columns || []);
      // Auto-select first phone column if detected
      if (response.data.suggested_column) {
        setSelectedColumn(response.data.suggested_column);
      }
    } catch (error) {
      console.error('Error getting columns:', error);
      toast.error('Could not read file columns');
    } finally {
      setLoadingColumns(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  }, [handleFileSelect]);

  const processFile = async () => {
    if (!file) return;
    if (!selectedColumn) {
      toast.error('Please select a phone column');
      return;
    }
    
    setProcessing(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('phone_column', selectedColumn);
      
      const response = await axios.post(
        `${API_URL}/api/tools/phone-formatter/process`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000
        }
      );
      
      setResult(response.data);
      
      if (response.data.total_rows > 100) {
        toast.info(`Formatted first 100 rows. ${response.data.total_rows - 100} rows remaining - sign up to format the rest.`);
      } else {
        toast.success('All phone numbers formatted successfully!');
      }
      
    } catch (error) {
      console.error('Processing error:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to process file';
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  const resetTool = () => {
    setFile(null);
    setResult(null);
    setColumns([]);
    setSelectedColumn('');
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Shared Header */}
      <Header />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-8 sm:pb-12">
        {/* Hero */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 rounded-full text-emerald-700 text-sm font-medium mb-4">
            <Phone className="w-4 h-4" />
            Free Phone Formatter
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-4">
            HubSpot Phone Number Fixer
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Format your phone numbers to E.164 international standard. 
            Perfect for HubSpot, Salesforce, and any CRM that requires clean phone data.
          </p>
        </div>

        {/* Features */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <Zap className="w-6 h-6 text-amber-500 mx-auto mb-2" />
            <p className="font-medium text-slate-900">E.164 Format</p>
            <p className="text-sm text-slate-500">International standard</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <CheckCircle className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
            <p className="font-medium text-slate-900">First 100 Free</p>
            <p className="text-sm text-slate-500">No signup required</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <Shield className="w-6 h-6 text-indigo-500 mx-auto mb-2" />
            <p className="font-medium text-slate-900">Side-by-Side</p>
            <p className="text-sm text-slate-500">Original + Cleaned</p>
          </div>
        </div>

        {!result ? (
          /* Upload Section */
          <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
            {/* Dropzone */}
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer
                ${file ? 'border-emerald-400 bg-emerald-50' : 'border-slate-300 hover:border-emerald-400 hover:bg-slate-50'}`}
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
              data-testid="phone-formatter-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => handleFileSelect(e.target.files[0])}
                className="hidden"
                data-testid="phone-formatter-file-input"
              />
              
              {file ? (
                <div className="space-y-3">
                  <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center mx-auto">
                    <FileSpreadsheet className="w-7 h-7 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{file.name}</p>
                    <p className="text-sm text-slate-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); resetTool(); }}
                  >
                    Change file
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mx-auto">
                    <Upload className="w-7 h-7 text-slate-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Drop your HubSpot export here</p>
                    <p className="text-sm text-slate-500">CSV or Excel file (max 50MB)</p>
                  </div>
                </div>
              )}
            </div>

            {/* Column Selection */}
            {file && columns.length > 0 && (
              <div className="mt-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Select Phone Column
                </label>
                {loadingColumns ? (
                  <div className="flex items-center gap-2 text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Loading columns...</span>
                  </div>
                ) : (
                  <select
                    value={selectedColumn}
                    onChange={(e) => setSelectedColumn(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                    data-testid="phone-column-select"
                  >
                    <option value="">-- Select a column --</option>
                    {columns.map((col) => (
                      <option key={col} value={col}>
                        {col}
                      </option>
                    ))}
                  </select>
                )}
                {selectedColumn && (
                  <p className="mt-2 text-sm text-emerald-600 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4" />
                    Selected: {selectedColumn}
                  </p>
                )}
              </div>
            )}

            {/* Process Button */}
            {file && (
              <Button
                onClick={processFile}
                disabled={processing || !selectedColumn}
                className="w-full mt-6 h-14 text-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-slate-300 disabled:cursor-not-allowed"
                data-testid="phone-formatter-process-btn"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Formatting Phone Numbers...
                  </>
                ) : !selectedColumn ? (
                  <>
                    <Phone className="w-5 h-5 mr-2" />
                    Select a Column First
                  </>
                ) : (
                  <>
                    <Phone className="w-5 h-5 mr-2" />
                    Format Phone Numbers (Free)
                  </>
                )}
              </Button>
            )}

            {/* How it works */}
            <div className="mt-8 pt-6 border-t border-slate-100">
              <h3 className="font-semibold text-slate-900 mb-4">What this tool does:</h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>Detects phone columns automatically (Phone, Mobile, Telephone...)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>Strips symbols and spaces, formats to E.164 (+1234567890)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>Outputs original AND cleaned phone side-by-side</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>Notes when country code couldn&apos;t be determined</span>
                </li>
              </ul>
              
              {/* Pro Tip */}
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <div className="flex items-start gap-3">
                  <Globe className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-amber-900">Pro Tip: Add a Country Column</p>
                    <p className="text-sm text-amber-700 mt-1">
                      For better results, include a &quot;Country&quot; column in your CSV with country codes (US, UK, FR, DE...). 
                      This helps us accurately detect the correct country code for local phone numbers like &quot;06 12 34 56 78&quot;.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Results Section */
          <div className="space-y-6">
            {/* Stats Card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <Phone className="w-7 h-7 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">Phone Numbers Formatted!</h2>
                  <p className="text-slate-500">{result.file_name}</p>
                </div>
              </div>

              <div className="grid sm:grid-cols-4 gap-4 mb-6">
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-slate-900">{result.total_rows}</p>
                  <p className="text-sm text-slate-500">Total Rows</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{result.formatted_count}</p>
                  <p className="text-sm text-slate-500">Formatted</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-amber-600">{result.uncertain_count}</p>
                  <p className="text-sm text-slate-500">Uncertain</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-slate-400">{result.empty_count}</p>
                  <p className="text-sm text-slate-500">Empty</p>
                </div>
              </div>

              {/* Preview Table */}
              <div className="border border-slate-200 rounded-xl overflow-hidden mb-6">
                <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                  <p className="font-medium text-slate-700">Preview (first 5 rows)</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Row</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Original Phone</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Cleaned Phone</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Extension</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {result.preview?.map((row, i) => (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="px-4 py-3 text-slate-500">#{row.row}</td>
                          <td className="px-4 py-3 font-mono text-slate-600">{row.original || '-'}</td>
                          <td className="px-4 py-3 font-mono text-emerald-600 font-medium">{row.cleaned || '-'}</td>
                          <td className="px-4 py-3 font-mono text-blue-600">{row.extension || '-'}</td>
                          <td className="px-4 py-3 text-slate-500 text-xs">{row.notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Download Button - With Email Capture for Anonymous Users */}
              <Button 
                onClick={() => {
                  if (user?.email) {
                    // Logged in - direct download
                    window.open(`${API_URL}${result.download_url}`, '_blank');
                    toast.success('Download started!');
                  } else {
                    // Anonymous - show email capture modal
                    setShowEmailModal(true);
                  }
                }}
                className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <Download className="w-5 h-5 mr-2" />
                Download Formatted File
              </Button>
            </div>

            {/* Freemium Upsell */}
            {result.total_rows > 100 && (
              <div className="bg-gradient-to-br from-indigo-600 to-purple-700 rounded-2xl p-6 sm:p-8 text-white">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-6 h-6" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-2">
                      We formatted the first 100 rows for free!
                    </h3>
                    <p className="text-indigo-100 mb-4">
                      To format the remaining <span className="font-bold text-white">{result.total_rows - 100} rows</span> and 
                      access 50+ other cleaning rules (email validation, name formatting, country standardization...), 
                      sign up for free.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Link to="/login">
                        <Button className="bg-white text-indigo-600 hover:bg-indigo-50 w-full sm:w-auto">
                          Sign Up Free
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* All rows processed */}
            {result.total_rows <= 100 && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 text-center">
                <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <h3 className="text-lg font-semibold text-emerald-800 mb-2">
                  All {result.total_rows} rows formatted successfully!
                </h3>
                <p className="text-emerald-700 mb-4">
                  Need more cleaning? Try our full data cleaner with 50+ rules.
                </p>
                <Link to="/login">
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    Try Full Data Cleaner (Free)
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            )}

            {/* Process Another */}
            <div className="text-center">
              <Button variant="ghost" onClick={resetTool}>
                Format Another File
              </Button>
            </div>
          </div>
        )}

        {/* Trust Badges */}
        {!result && (
          <div className="mt-8 flex items-center justify-center gap-6 text-sm text-slate-500">
            <div className="flex items-center gap-1">
              <Shield className="w-4 h-4" />
              <span>Secure processing</span>
            </div>
            <div className="flex items-center gap-1">
              <CheckCircle className="w-4 h-4" />
              <span>No signup required</span>
            </div>
          </div>
        )}
      </main>
      
      {/* Email Capture Modal for Anonymous Users */}
      <EmailCaptureModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSuccess={(downloadUrl) => {
          window.open(`${API_URL}${result.download_url}`, '_blank');
          toast.success('Download started!');
          setShowEmailModal(false);
        }}
        tool="phone-formatter"
        fileId={result?.file_id || result?.download_url?.split('/').pop()}
        toolName="Phone Formatter"
        rowsProcessed={result?.total_rows}
        accentColor="emerald"
      />
      
      <SupportButton />
    </div>
  );
};

export default PhoneFormatterPage;
